import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Sparkles, MapPin, Phone, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { sendChat } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { log } from '../lib/logger';
import { SCHOOL, LOGO_URL } from '../lib/constants';
import { useLeadModal } from '../lib/leadModalContext';

const SESSION_KEY = 'ukk_chat_session_id';
const HISTORY_KEY = 'ukk_chat_messages';

const SUGGESTED = [
  'What classes do you offer for kids?',
  'How much does it cost?',
  'Where are you located?',
  'Tell me about your belt ranks',
];

const WELCOME = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I\u2019m Professor AI \u2014 your guide at Universal Kempo Karate. Ask me about classes, schedule, pricing, belt ranks, the team, or the history of Kempo. Ready to get started? I\u2019ll help you book a Free Trial.",
  quick_actions: ['Book Free Trial', 'Get Directions'],
};

const ChatWidget = () => {
  const { openLeadModal } = useLeadModal();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Restore session + history
  useEffect(() => {
    try {
      const sid = localStorage.getItem(SESSION_KEY);
      if (sid) setSessionId(sid);
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed);
      }
    } catch (e) {
      log.warn('Failed to restore chat session:', e);
    }
  }, []);

  // Persist messages
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
    } catch (e) {
      log.warn('Failed to persist chat history:', e);
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleQuickAction = (label) => {
    if (label === 'Book Free Trial') {
      openLeadModal('chat-quick-action');
      return;
    }
    if (label === 'Get Directions') {
      window.open(SCHOOL.directionsUrl, '_blank', 'noopener');
      return;
    }
    if (label === 'Call / Text' || label === 'Call/Text') {
      window.location.href = SCHOOL.phoneHref;
      return;
    }
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setInput('');
    setSending(true);
    trackEvent('chat_send', { length: trimmed.length });
    const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages((m) => [...m, { id: userMsgId, role: 'user', content: trimmed }]);
    try {
      const resp = await sendChat(sessionId, trimmed);
      if (resp.session_id && resp.session_id !== sessionId) {
        setSessionId(resp.session_id);
        try { localStorage.setItem(SESSION_KEY, resp.session_id); } catch (e) { log.warn('Failed to persist chat session_id', e); }
      }
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role: 'assistant', content: resp.reply, quick_actions: resp.quick_actions || [] },
      ]);
    } catch (e) {
      log.error('Chat send failed:', e);
      setMessages((m) => [
        ...m,
        {
          id: `e-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          role: 'assistant',
          content:
            "I had trouble connecting just now. You can call or text us directly at " +
            SCHOOL.phone +
            ", or tap Book Free Trial below.",
          quick_actions: ['Book Free Trial', 'Call / Text'],
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Orb */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="orb"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => { setOpen(true); trackEvent('chat_open'); }}
            className="fixed bottom-24 sm:bottom-20 right-5 z-[10000] h-16 w-16 rounded-full bg-[color:var(--neon-red)] text-white grid place-items-center chat-orb-pulse hover:bg-[#ff3a3a]"
            data-testid="chat-widget-open-button"
            aria-label="Open AI Chat Assistant"
          >
            <MessageSquare className="h-7 w-7" />
            <span className="sr-only">Open chat</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="fixed bottom-24 right-3 sm:bottom-5 sm:right-5 z-[10000] w-[calc(100vw-1.5rem)] sm:w-[400px] h-[calc(100dvh-9rem)] sm:h-[600px] max-h-[680px] panel-solid rounded-2xl flex flex-col overflow-hidden border border-[color:var(--neon-red)]/35"
            style={{ boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 30px 80px rgba(0,0,0,0.6)' }}
            data-testid="chat-widget-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/30">
              <div className="h-9 w-9 rounded-full overflow-hidden glow-red-sm">
                <img src={LOGO_URL} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="font-display text-sm text-white tracking-tight">Professor AI</div>
                <div className="text-[10px] font-mono-ui uppercase tracking-[0.18em] text-[color:var(--neon-cyan)] flex items-center gap-1.5">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--neon-cyan)]" /> Online
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 grid place-items-center rounded-lg text-white/70 hover:bg-white/5"
                data-testid="chat-widget-close-button"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1">
              <div
                ref={scrollRef}
                className="px-4 py-4 space-y-3 overflow-y-auto h-full"
                data-testid="chat-messages"
              >
                {messages.map((m, idx) => (
                  <div
                    key={m.id || `msg-${idx}`}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-white/10 text-white border border-white/10'
                          : 'bg-[rgba(255,42,42,0.10)] text-white border border-[color:var(--neon-red)]/25'
                      }`}
                      data-testid={`chat-message-${m.role}-${idx}`}
                    >
                      {m.content}
                      {m.role === 'assistant' && Array.isArray(m.quick_actions) && m.quick_actions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {m.quick_actions.map((a) => (
                            <button
                              key={a}
                              onClick={() => handleQuickAction(a)}
                              className="inline-flex items-center gap-1.5 text-[11px] font-display uppercase tracking-[0.14em] border border-[color:var(--neon-red)]/45 text-white bg-[color:var(--neon-red)]/15 hover:bg-[color:var(--neon-red)]/25 rounded-full px-2.5 py-1"
                              data-testid={`chat-quickaction-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                            >
                              {a === 'Book Free Trial' && <Sparkles className="h-3 w-3" />}
                              {a === 'Get Directions' && <MapPin className="h-3 w-3" />}
                              {(a === 'Call / Text' || a === 'Call/Text') && <Phone className="h-3 w-3" />}
                              {a}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-[rgba(255,42,42,0.10)] border border-[color:var(--neon-red)]/25 text-white rounded-2xl px-3 py-2 text-sm flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span className="text-white/80">Professor is typing…</span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Suggested chips (first turn) */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {SUGGESTED.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-[11px] font-display uppercase tracking-[0.12em] text-white/85 border border-white/15 bg-white/5 hover:bg-white/10 rounded-full px-2.5 py-1"
                    data-testid={`chat-suggested-${s.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-white/10 bg-black/30 flex items-center gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about classes, pricing, schedule…"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/45 focus-visible:ring-[color:var(--neon-red)]"
                data-testid="chat-message-input"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="h-10 w-10 grid place-items-center rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] disabled:opacity-50 disabled:cursor-not-allowed text-white glow-red-sm"
                data-testid="chat-send-button"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
